/* Assets / Media Library — central image & document store used by every
 * Image picker across the CMS. Supports upload, list, categorize, copy URL,
 * delete. */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, resolveAssetUrl, formatApiError } from "@/lib/api";
import { ChevronLeft, Upload, Search, Trash2, Copy, Check, ImageIcon } from "lucide-react";
import { useDataLoader } from "./_useDataLoader";

const CATEGORIES = [
  { value: "", label: "All categories" },
  { value: "logo", label: "Logo" },
  { value: "founder", label: "Founder" },
  { value: "founding_member", label: "Founding Member" },
  { value: "book_cover", label: "Book Cover" },
  { value: "media", label: "Media" },
  { value: "recognition", label: "Recognition Proof" },
  { value: "legal", label: "Legal / Document" },
  { value: "page", label: "Page / Section" },
  { value: "other", label: "Other" },
];

const UPLOAD_CATEGORIES = CATEGORIES.filter((c) => c.value);

export default function AdminAssets() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [uploadCategory, setUploadCategory] = useState("other");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await adminApi.assetsList({
        category: category || undefined,
        q: q || undefined,
      });
      setItems(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not load assets.");
    }
  };

  useEffect(() => {
    document.title = "BSCA Admin · Assets";
  }, []);
  useDataLoader(load, [category]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setErr("");
    try {
      for (const f of files) {
        await adminApi.assetsUpload(f, uploadCategory);
      }
      await load();
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateCategory = async (a, newCat) => {
    try {
      const updated = await adminApi.assetsPatch(a.id, { category: newCat });
      setItems((prev) => prev.map((p) => (p.id === a.id ? updated : p)));
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Update failed.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.original_name || a.filename}"? This will remove the file permanently.`)) return;
    try {
      await adminApi.assetsDelete(a.id);
      setItems((prev) => prev.filter((p) => p.id !== a.id));
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Delete failed.");
    }
  };

  const copyUrl = (a) => {
    const u = a.url;
    navigator.clipboard?.writeText(u);
    setCopiedId(a.id);
    setTimeout(() => setCopiedId(""), 1500);
  };

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link to="/admin/dashboard" className="bsca-link text-xs text-[#4B5563] inline-flex items-center">
            <ChevronLeft className="h-3 w-3 mr-1" /> Dashboard
          </Link>
          <h1 className="mt-3 font-serif text-4xl tracking-tight">Assets / Media Library</h1>
          <p className="text-[#4B5563] text-sm mt-1">{items.length} {items.length === 1 ? "asset" : "assets"} · Images & PDFs · Max 12 MB</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            data-testid="upload-category"
            value={uploadCategory}
            onChange={(e) => setUploadCategory(e.target.value)}
            className="bg-white border border-black/10 px-3 py-2.5 text-sm rounded-sm"
          >
            {UPLOAD_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>Upload as: {c.label}</option>
            ))}
          </select>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden multiple onChange={onPick} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="btn-upload-asset"
            className="bsca-lift bg-[#D4AF37] text-[#050B14] font-medium px-5 py-3 rounded-sm inline-flex items-center disabled:opacity-60"
          >
            <Upload className="h-4 w-4 mr-2" /> {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <select
          data-testid="filter-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-white border border-black/10 px-3 py-2 text-sm rounded-sm"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <div className="flex items-center bg-white border border-black/10 rounded-sm px-3">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by filename…"
            data-testid="search-assets"
            className="px-2 py-2 text-sm outline-none w-56"
          />
        </div>
        <button onClick={load} className="text-xs bsca-link">Search</button>
      </div>

      {err && <div className="text-red-700 text-sm mb-4 border border-red-300 bg-red-50 px-4 py-2 rounded-sm">{err}</div>}

      {items.length === 0 ? (
        <div className="bg-white border border-black/10 p-12 text-center text-[#9CA3AF]">
          <ImageIcon className="h-8 w-8 mx-auto mb-3 text-[#D4AF37]" />
          No assets yet. Use Upload to add logos, portraits, book covers, PDFs and more.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`asset-card-${a.id}`} className="bg-white border border-black/10 group flex flex-col">
              <div className="aspect-square bg-[#F8F6F1] flex items-center justify-center overflow-hidden">
                {a.mime?.startsWith("image/") ? (
                  <img src={resolveAssetUrl(a.url)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs text-[#4B5563] p-3 text-center break-all">{a.original_name}</span>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="text-[11px] truncate text-[#4B5563]" title={a.original_name}>{a.original_name}</div>
                <select
                  value={a.category}
                  onChange={(e) => updateCategory(a, e.target.value)}
                  data-testid={`asset-category-${a.id}`}
                  className="w-full text-[11px] bg-[#F8F6F1] border border-black/10 px-2 py-1 rounded-sm"
                >
                  {UPLOAD_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => copyUrl(a)}
                    data-testid={`btn-copy-${a.id}`}
                    className="text-[11px] text-[#4B5563] hover:text-[#050B14] inline-flex items-center gap-1"
                    title="Copy URL"
                  >
                    {copiedId === a.id ? <Check className="h-3 w-3 text-green-700" /> : <Copy className="h-3 w-3" />}
                    {copiedId === a.id ? "Copied" : "Copy URL"}
                  </button>
                  <button
                    onClick={() => remove(a)}
                    data-testid={`btn-delete-asset-${a.id}`}
                    className="text-[11px] text-[#9CA3AF] hover:text-red-600 inline-flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
